/**
 * JSON-shaped value used at an untyped API boundary. `undefined` represents an
 * omitted object member before the value is serialized; it is not valid JSON on
 * the wire.
 */
export type JsonValue =
  | boolean
  | null
  | number
  | string
  | readonly JsonValue[]
  | JsonObject;

/** JSON-shaped object with recursively serializable values. */
export interface JsonObject {
  readonly [key: string]: JsonValue | undefined;
}

const isJsonValueInternal = (
  value: unknown,
  ancestors: Set<object>,
): value is JsonValue => {
  if (
    value === null ||
    typeof value === "boolean" ||
    typeof value === "number" ||
    typeof value === "string"
  ) {
    return true;
  }
  if (typeof value !== "object" || ancestors.has(value)) return false;

  ancestors.add(value);
  const isValid = Array.isArray(value)
    ? value.every((entry) => isJsonValueInternal(entry, ancestors))
    : Object.values(value).every(
        (entry) => entry === undefined || isJsonValueInternal(entry, ancestors),
      );
  ancestors.delete(value);
  return isValid;
};

/** Runtime validation for data that will be serialized as JSON. */
export const isJsonValue = (value: unknown): value is JsonValue =>
  isJsonValueInternal(value, new Set());

export const isJsonObject = (value: unknown): value is JsonObject =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  isJsonValue(value);
